import { Calendar, CheckCircle, PhoneOutgoing, Settings } from 'lucide-react'
import { useTranslation, Trans } from 'react-i18next'
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

interface Props {
  isOpen: boolean
  onClose: () => void
  agentName: string
  onGoToActions: () => void
  /** Ouvre la modale « Appelez-moi » — le moment idéal pour entendre l'agent. */
  onTestCall?: () => void
}

/** Port 1:1 du Next AgentCreatedModal — célébration post-création. */
export function AgentCreatedModal({
  isOpen,
  onClose,
  agentName,
  onGoToActions,
  onTestCall,
}: Props) {
  const { t } = useTranslation()
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="sr-only">
            {t('manager.aiAgents.created.title')}
          </DialogTitle>
        </DialogHeader>
        <div className="p-6">
          <div className="text-center mb-6">
            <div className="relative mx-auto w-24 h-24 mb-6">
              <Avatar className="h-24 w-24 border-4 border-green-100">
                <AvatarImage
                  src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${agentName}`}
                />
                <AvatarFallback className="bg-blue-100 text-blue-700 text-2xl">
                  {agentName.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-green-500 flex items-center justify-center border-4 border-white shadow-sm">
                <CheckCircle className="h-5 w-5 text-white" />
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-1">
              {t('manager.aiAgents.created.congratulations', { name: agentName })}
            </h2>
          </div>

          <p className="text-sm text-gray-600 text-center mb-6">
            <Trans
              i18nKey="manager.aiAgents.created.needAccess"
              values={{ name: agentName }}
              components={{ b: <strong /> }}
            />
          </p>

          <div className="bg-blue-50 rounded-lg p-4 mb-6 space-y-3">
            <div className="flex items-start gap-3 p-2 rounded-md hover:bg-blue-100/50 transition-colors">
              <div className="flex-shrink-0 mt-0.5">
                <div className="w-8 h-8 rounded-lg bg-white border-2 border-blue-200 flex items-center justify-center">
                  <Calendar className="h-4 w-4 text-blue-600" />
                </div>
              </div>
              <div className="flex-1">
                <div className="font-medium text-sm text-gray-900">
                  {t('manager.aiAgents.created.connectCalendar')}
                </div>
                <div className="text-xs text-gray-600 mt-0.5">
                  {t('manager.aiAgents.created.forAppointments')}
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3 p-2 rounded-md hover:bg-blue-100/50 transition-colors">
              <div className="flex-shrink-0 mt-0.5">
                <div className="w-8 h-8 rounded-lg bg-white border-2 border-blue-200 flex items-center justify-center">
                  <Settings className="h-4 w-4 text-blue-600" />
                </div>
              </div>
              <div className="flex-1">
                <div className="font-medium text-sm text-gray-900">
                  {t('manager.aiAgents.created.enablePermissions')}
                </div>
                <div className="text-xs text-gray-600 mt-0.5">
                  {t('manager.aiAgents.created.forCancelReschedule')}
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            {onTestCall && (
              <Button
                type="button"
                onClick={onTestCall}
                className="w-full bg-green-600 hover:bg-green-700 h-auto py-3 flex-col items-center"
              >
                <span className="flex items-center gap-2 font-semibold">
                  <PhoneOutgoing className="h-4 w-4" />
                  {t('manager.aiAgents.testCall.createdCta')}
                </span>
                <span className="text-[11px] font-normal opacity-90">
                  {t('manager.aiAgents.testCall.createdCtaHint')}
                </span>
              </Button>
            )}
            <Button
              type="button"
              onClick={onGoToActions}
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              {t('manager.aiAgents.created.giveAccess')}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="w-full"
            >
              {t('manager.aiAgents.created.later')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
